import { sanitizeUrl } from '@l3ns/shared/constants';
import type { DeviceClass, WireSession, WireUtm } from '@l3ns/shared/types';
import { st } from './state';

// ordered: first match wins (iOS before macOS — iPad UAs contain "Mac OS X";
// Android before Linux). Capture group 1 = version.
const BROWSERS: [string, RegExp][] = [
	['Edge', /Edg\/([\d.]+)/],
	['Opera', /OPR\/([\d.]+)/],
	['Firefox', /Firefox\/([\d.]+)/],
	['Chrome', /Chrome\/([\d.]+)/],
	['Safari', /Version\/([\d.]+).*Safari\//],
];
const OSES: [string, RegExp][] = [
	['Windows', /Windows NT ([\d.]+)/],
	['iOS', /(?:iPhone|iPad|iPod).*OS ([\d_]+)/],
	['Chrome OS', /CrOS/],
	['macOS', /Mac OS X ([\d_.]+)/],
	['Android', /Android ([\d.]+)/],
	['Linux', /Linux/],
];

function match(ua: string, table: [string, RegExp][]): [string, string?] {
	for (const [name, re] of table) {
		const m = ua.match(re);
		if (m) return [name, m[1] && m[1].replace(/_/g, '.')];
	}
	return ['Other'];
}

/**
 * `touch` = navigator.maxTouchPoints: iPadOS asks for desktop sites with a
 * Macintosh UA, and no Mac has a touch screen — so a touching "Mac" is an
 * iPad (os iOS, version unknown, tablet).
 */
export function parseUa(
	ua: string,
	touch = 0,
): {
	browser: string;
	browserVersion?: string;
	os: string;
	osVersion?: string;
	device: DeviceClass;
} {
	const [browser, browserVersion] = match(ua, BROWSERS);
	let [os, osVersion] = match(ua, OSES);
	let device: DeviceClass = /iPad|Tablet|(?:Android(?!.*Mobile))/.test(ua)
		? 'tablet'
		: /Mobi|iPhone|Android/.test(ua)
			? 'mobile'
			: 'desktop';
	if (os == 'macOS' && touch > 1) [os, osVersion, device] = ['iOS', undefined, 'tablet'];
	return { browser, browserVersion, os, osVersion, device };
}

// -- OS version via UA client hints ---------------------------------------------
// Chromium's reduced UA string freezes Android at "10" and macOS at "10.15.7"
// (Safari freezes macOS too), so on those browsers the real OS version only
// comes from the async high-entropy `platformVersion` hint. Until it resolves
// the version is left unknown — the consumer keeps the first KNOWN value it
// sees, so a later envelope fills it in; unknown beats wrong. The raw hint
// ("13.0.0" = Windows 11, "14.4.1", "14.0.0") is normalized server-side
// (apps/api lib/ua.ts), like the UA-string version.
type Uad = { getHighEntropyValues?: (h: string[]) => Promise<{ platformVersion?: string }> };
const uad = (): Uad | undefined => (navigator as { userAgentData?: Uad }).userAgentData;
/** platformVersion, once known. */
let hint: string | undefined;
/** The UA string's own version of the current session's OS. */
let uaVer: string | undefined;

export function osVersionOf(uaVersion?: string): string | undefined {
	// hint resolved → the real one; hints available but pending → unknown for
	// now; no hints at all (Safari, Firefox, iOS) → the UA string is all there is
	return hint || (uad()?.getHighEntropyValues ? undefined : uaVersion);
}

export function initHints(): void {
	try {
		void uad()
			?.getHighEntropyValues?.(['platformVersion'])
			.then(
				(h) => {
					hint = h.platformVersion;
					const s = st.session_;
					if (s) s.osVersion = osVersionOf(uaVer);
				},
				() => {},
			);
	} catch {}
}

/**
 * navigator.connection.effectiveType — 'slow-2g'|'2g'|'3g'|'4g' (the spec caps
 * at 4g: 5G/WiFi/fiber all report '4g'). Absent on Safari/Firefox. Read fresh
 * at every flush so mid-session network switches show up on later envelopes.
 */
export function ect(): string | undefined {
	try {
		const t = (navigator as { connection?: { effectiveType?: unknown } }).connection?.effectiveType;
		return typeof t == 'string' ? t.toLowerCase().slice(0, 16) : undefined;
	} catch {
		return undefined;
	}
}

/** sessionStorage key of the signed-in user (`[id, name]`, `[]` = guest) — per tab, see core/user.ts. */
export const KEY_USER = '_l3ns_u';

export function readUtm(search: string): WireUtm | undefined {
	try {
		const p = new URLSearchParams(search);
		const utm: WireUtm = {};
		let any = false;
		for (const k of 'source,medium,campaign,term,content'.split(',') as (
			'source' | 'medium' | 'campaign' | 'term' | 'content'
		)[]) {
			const v = p.get('utm_' + k);
			if (v) {
				utm[k] = v.slice(0, 200);
				any = true;
			}
		}
		return any ? utm : undefined;
	} catch {
		return undefined;
	}
}

/** Build the per-envelope session context from the live browser environment. */
export function buildSession(
	id: string,
	startedAt: number,
	isNew: boolean,
	isReturning: boolean,
	release?: string,
): WireSession {
	let tz: string | undefined;
	try {
		tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch {}
	// a user signed in earlier in this tab stays signed in across reloads,
	// SPA routes and the idle rotation — the tab closing or a sign-out ends it
	let [uid, uname]: [string?, string?] = [];
	try {
		[uid, uname] = JSON.parse(sessionStorage.getItem(KEY_USER) || '[]') as [string?, string?];
	} catch {}
	const ua = parseUa(navigator.userAgent || '', navigator.maxTouchPoints);
	uaVer = ua.osVersion;
	return {
		id,
		startedAt,
		isNew,
		isReturning,
		uid,
		uname,
		// sanitized like every other URL — a landing on '/welcome?token=…' or an
		// auth callback must not persist the secret for the session's lifetime
		entryUrl: sanitizeUrl(location.href),
		referrer: sanitizeUrl(document.referrer) || undefined,
		utm: readUtm(location.search),
		...ua,
		osVersion: osVersionOf(uaVer),
		screenW: screen?.width,
		screenH: screen?.height,
		lang: navigator.language?.slice(0, 35),
		tz,
		release: release?.slice(0, 120) || undefined,
		conn: ect(),
	};
}
